import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { MarketingLayout, Section } from "@/components/marketing/MarketingLayout";
import api from "@/lib/api";
import { inrFull } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const SECTIONS = ["All", "Materials", "Services", "Equipment", "Professionals"];

export default function MarketplacePublic() {
  const [products, setProducts] = useState(null);
  const [section, setSection] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = {};
    if (section !== "All") params.section = section;
    if (q) params.q = q;
    api.get("/marketplace", { params }).then(({ data }) => setProducts(data));
  }, [section, q]);

  return (
    <MarketingLayout>
      <section className="bg-sp-navy text-white">
        <div className="sp-container py-20">
          <div className="sp-eyebrow mb-3">Sitepar Market</div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">Everything your project needs.</h1>
          <p className="mt-4 text-white/65 max-w-xl">Verified building materials, services, equipment and professionals — connected to real construction projects.</p>
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="market-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search materials, brands or services..."
                   className="pl-11 h-12 bg-white/10 border-white/15 text-white placeholder:text-white/40 rounded-full" />
          </div>
        </div>
      </section>

      <Section>
        <div className="flex flex-wrap gap-2 mb-8">
          {SECTIONS.map((s) => (
            <button key={s} data-testid={`market-tab-${s.toLowerCase()}`} onClick={() => setSection(s)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${section === s ? "bg-sp-navy text-white border-sp-navy" : "border-slate-200 text-slate-600 hover:border-sp-orange"}`}>
              {s}
            </button>
          ))}
        </div>

        {!products ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}</div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="market-empty">No products match your search.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="market-grid">
            {products.map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-200 p-5 sp-hover-card bg-white" data-testid={`market-product-${p.id}`}>
                <div className="text-[11px] uppercase tracking-wider text-sp-orange font-semibold">{p.category}</div>
                <div className="font-semibold text-sp-navy mt-1 leading-snug">{p.name}</div>
                <div className="text-xs text-slate-400 mt-1">{p.seller} · {p.location}</div>
                <div className="mt-4 flex items-end justify-between">
                  <div><span className="text-lg font-bold text-sp-navy">{inrFull(p.price)}</span><span className="text-xs text-slate-400">/{p.unit}</span></div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">{p.availability}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </MarketingLayout>
  );
}
